/* eslint-disable max-lines-per-function, max-lines */
import { store } from "jiffy-store";
import { camelize } from "common/utils/helpers/cases";
import { formatCurrency } from "common/utils/format/number";
import { trackRudderstackBlanksProductPriceChanged } from "common/utils/rudderstack/tracker";
import productPage from "./product-page";

const MIN_ITEMS_AMOUNT = 1;
const MAX_ITEMS_AMOUNT = 9999;
let currentVariantIDs = [];

const getNewBulkPricePriority = bulkPricePriority => {
  const salePriority = parseInt($("#product-details-variants-form").data().saleAmountPriority, 10);

  return Math.max(bulkPricePriority, salePriority);
};

const getNewPrice = (variantId, addedItemsAmount, bulkPricePriority) => {
  let newPrice = null;
  let decorationCosts;
  let totalItemsAmount = parseInt(window.itemsInCart, 10) + parseInt(addedItemsAmount, 10);
  const newPriority = getNewBulkPricePriority(bulkPricePriority);
  const tierPrices = productPage.variantPricesData
    ? productPage.variantPricesData[variantId]
    : productPage.variantPricesCountData[variantId];
  const variantDecorationPrices = productPage.decorationPricesForVariants[variantId];

  if (variantDecorationPrices) {
    decorationCosts = variantDecorationPrices[productPage.projectColor];
  }

  if (totalItemsAmount > MAX_ITEMS_AMOUNT) {
    totalItemsAmount = MAX_ITEMS_AMOUNT;
  }

  if (decorationCosts && totalItemsAmount > MIN_ITEMS_AMOUNT) {
    const decorationCost = decorationCosts.find(
      elem => elem.min <= totalItemsAmount && elem.max >= totalItemsAmount
    ).price;
    newPrice = (parseFloat(tierPrices[newPriority]) + decorationCost).toFixed(2);
  } else if (!decorationCosts) {
    newPrice = tierPrices[newPriority];
  }

  return newPrice;
};

const getNewAreaPrice = (variantId, bulkPricePriority) => {
  const tierPrices = productPage.variantPrintAreaPrice?.[variantId];

  if (!tierPrices) return null;

  const newPriority = getNewBulkPricePriority(bulkPricePriority);

  return tierPrices[newPriority];
};

// eslint-disable-next-line max-params
const updateVariantSellingPrice = ({
  variantId,
  newPrice,
  $priceElement,
  addedItemsAmount,
  colorName,
  inputVariantId,
  currentPrice
}) => {
  const price = parseFloat(newPrice);
  const matches = $priceElement
    .text()
    .trim()
    .match(/([^\d]*)([\d,]*\.\d{2})/);

  if (!matches) {
    return;
  }

  const symbol = matches[1];
  const oldPrice = parseFloat(matches[2]);

  if (variantId === inputVariantId && currentPrice !== price) {
    trackRudderstackBlanksProductPriceChanged({
      variantId,
      price,
      quantity: addedItemsAmount,
      variant: colorName
    });
  }

  if (!$priceElement.data("processed")) {
    $priceElement.data("processed", true);
    $priceElement.data("old-value", oldPrice);
    if (oldPrice !== price) {
      $priceElement
        .hide()
        .addClass($priceElement.data("highlight-class"))
        .children("span")
        .text(formatCurrency(price, symbol));

      $priceElement.fadeIn(1000, () =>
        $priceElement.removeClass($priceElement.data("highlight-class"))
      );
    }
  } else if (price > oldPrice) {
    $priceElement.text(formatCurrency(price, symbol));
    if ($priceElement.data("old-value") === `${price}`) {
      $priceElement
        .show()
        .removeClass($priceElement.data("highlight-class"))
        .children("span")
        .text(formatCurrency(price, symbol));
    }
  }
};

const updateVariantRetailPrice = (newPrice, $element) => {
  const price = parseFloat(newPrice);
  const matches = $element
    .text()
    .trim()
    .match(/([^\d]*)([\d,]*\.\d{2})/);
  if (!matches) {
    return;
  }

  const symbol = matches[1];
  const oldPrice = parseFloat(matches[2]);
  if (price === oldPrice) return;

  $element.text(formatCurrency(price, symbol));
  $element.data("price", price);
};

const setDtfElementPrice = (newPrice, $element) => {
  const currencySymbol = $element.data("currencySymbol");
  const currencyDecimals = $element.data("currencyDecimals");
  if (!currencySymbol) return;

  $element.text(formatCurrency(newPrice, currencySymbol, currencyDecimals));
};

const updateVariantOffValue = (variantId, newValue, $element) => {
  $element?.text(`${newValue}`);
};

export default (
  bulkPricePriority,
  form,
  {
    colorName = null,
    formSubmit = false,
    addedItemsAmount = 0,
    inputVariantId = null,
    variantId = null,
    variantsQuantity = {}
  } = {}
) => {
  const formVariantDomStructure = store.get(state => state.pdp[camelize(form.getAttribute("id"))]);

  if (colorName !== "White" && colorName !== null) {
    productPage.projectColor = "Dark";
  }

  if (inputVariantId) {
    const { input: currentVariantInput } = formVariantDomStructure[inputVariantId];

    currentVariantIDs = currentVariantIDs.filter(idx => idx !== inputVariantId);

    if (currentVariantInput.value) {
      let lastAddedId = inputVariantId;

      if (currentVariantIDs.length > 0) {
        lastAddedId = currentVariantIDs[currentVariantIDs.length - 1];
      }

      if (
        getNewPrice(inputVariantId, addedItemsAmount, bulkPricePriority) <
        getNewPrice(lastAddedId, addedItemsAmount, bulkPricePriority)
      ) {
        currentVariantIDs = [...currentVariantIDs, inputVariantId];
      } else {
        currentVariantIDs = [inputVariantId, ...currentVariantIDs];
      }
    }
  }

  if (productPage.variantPricesCountData) {
    if (!variantId) return;

    const { priceElement, percentOffElement, savingOffElement, msrp } =
      formVariantDomStructure[variantId];
    const newPrice = productPage.variantPricesCountData[variantId][bulkPricePriority];
    const $priceElement = $(priceElement);
    updateVariantSellingPrice({
      variantId,
      newPrice,
      $priceElement,
      addedItemsAmount: variantsQuantity[inputVariantId],
      colorName,
      inputVariantId
    });

    if (msrp && percentOffElement) {
      const newPercentOff = Math.round(((msrp - newPrice) / msrp) * 100);
      percentOffElement.parentElement?.classList?.remove("hidden");
      const $percentOffElement = $(percentOffElement);
      updateVariantOffValue(variantId, newPercentOff, $percentOffElement);
    }

    if (msrp && savingOffElement) {
      const amount = Math.round((msrp - newPrice) * 100.0) / 100.0;
      const newSavingOff = amount.toFixed(Math.max((`${amount}`.split(".")[1] || "").length, 2));
      const $savingOffElement = $(savingOffElement);
      updateVariantOffValue(variantId, newSavingOff, $savingOffElement);
    }

    $priceElement.removeData("processed");
  } else {
    const allVariantIDs = [
      ...new Set([...Object.keys(formVariantDomStructure), ...currentVariantIDs])
    ];

    allVariantIDs.forEach(id => {
      if (!formVariantDomStructure[id]) return;

      const $priceElement = $(formVariantDomStructure[id].priceElement);
      const matches = $priceElement
        .text()
        .trim()
        .match(/([^\d]*)([\d,]*\.\d{2})/);

      if (matches) {
        formVariantDomStructure[id].currentPrice = parseFloat(matches[2]);
      }

      const newPrice = getNewPrice(id, addedItemsAmount, bulkPricePriority);
      const newAreaPrice = getNewAreaPrice(id, bulkPricePriority);

      const newSellingPrice = newAreaPrice || newPrice;

      if (newSellingPrice && !(addedItemsAmount === 0 && formSubmit)) {
        const {
          priceElement: $price,
          percentOffElement: $percentOff,
          savingOffElement: $savingOff,
          msrpElement: $msrp,
          dtfPerInchPriceElement: $dtfPerInchPrice,
          dtfAreaPriceElement: $dtfAreaPrice,
          dtfAreaPriceTotalOldElement: $dtfAreaPriceTotalOld,
          dtfAreaPriceTotalElement: $dtfAreaPriceTotal,
          input: $input,
          currentPrice
        } = formVariantDomStructure[id];

        updateVariantSellingPrice({
          variantId: id,
          newPrice: newSellingPrice,
          $priceElement: $($price),
          addedItemsAmount: variantsQuantity[inputVariantId],
          colorName,
          inputVariantId,
          currentPrice
        });

        const { variantRetailPrices } = productPage;

        const msrp = variantRetailPrices?.[id] || formVariantDomStructure[id].msrp;

        if (msrp && $msrp) {
          updateVariantRetailPrice(msrp, $($msrp));
        }

        if (msrp && $percentOff) {
          const newPercentOff = Math.round(((msrp - newSellingPrice) / msrp) * 100);
          $percentOff.parentElement?.classList?.remove("hidden");
          updateVariantOffValue(id, newPercentOff, $($percentOff));
        }

        if (msrp && $savingOff) {
          const amount = Math.round((msrp - newSellingPrice) * 100.0) / 100.0;
          const newSavingOff = amount.toFixed(
            Math.max((`${amount}`.split(".")[1] || "").length, 2)
          );
          updateVariantOffValue(variantId, newSavingOff, $($savingOff));
        }

        // dtf section
        const inputValue = Number($input.value);

        if ($dtfPerInchPrice) {
          setDtfElementPrice(newPrice, $($dtfPerInchPrice));
        }

        if ($dtfAreaPrice && newAreaPrice) {
          setDtfElementPrice(newAreaPrice, $($dtfAreaPrice));
        }

        if ($dtfAreaPriceTotalOld) {
          const price = inputValue * Number($($msrp).data("price"));
          setDtfElementPrice(price, $($dtfAreaPriceTotalOld));
        }

        if ($dtfAreaPriceTotal) {
          $dtfAreaPriceTotal
            .closest(".js-dtf-total-block")
            ?.classList?.toggle("hidden", !inputValue);

          const roundedPrice = Number(newSellingPrice).toFixed(2);
          const price = inputValue * Number(roundedPrice);
          setDtfElementPrice(price, $($dtfAreaPriceTotal));
        }

        $($price).removeData("processed");
      }
    });
  }
};
